// Remembers which order the browser left for, so the return page can name it.
//
// The return from Meta's checkout host is a plain top-level navigation to a
// STATIC url registered on the app row — `/checkout/success` or
// `/checkout/failure`, with no order id and no signed outcome. Whether ECP
// appends a query parameter is unverified (D118981624 declares only the
// prefix), so the return page cannot rely on one being there.
//
// sessionStorage is the right shape for this: it is scoped to the tab, so it
// survives navigating away to payments.apnb-api.com and back, and it dies when
// the tab does. The order id is not a secret — it is already in the URL on
// `/checkout` — which is what makes this storable at all. The handoff CODE is a
// single-use bearer credential and must never come near this module.
//
// This is a display convenience, nothing more. It is not evidence of payment:
// the value is written before the user has paid anything, and anyone can edit
// it. Every fact shown on the return page comes from OUR server keyed on this
// id, and the read is authorized, so the worst a tampered value can do is show
// the tampering user one of their own orders, or nothing.

import { isApiRecord, type ApiValue } from '../../api/responseDecoders'
import { createNadDebug } from '../../utils/debugLog'

const log = createNadDebug('dw:billing-return')

const KEY = 'dw.checkout.pending'

interface PendingCheckout {
  orderId: string
  userId: string
}

/**
 * Note the order the browser is about to leave for.
 *
 * Stored with the user id so a second account signing in on the same tab does
 * not inherit the first one's pending order. The server would refuse to serve
 * it anyway; this just avoids the pointless request and the confusing state.
 */
export function rememberPendingCheckout(userId: string, orderId: string): void {
  try {
    const pending: PendingCheckout = { orderId, userId }
    window.sessionStorage.setItem(KEY, JSON.stringify(pending))
  } catch {
    // Private mode, a storage quota, or a browser with storage disabled. The
    // return page degrades to its "which order was that?" state, which is a
    // worse experience but a correct one. Not worth failing the payment over.
    log({ event: 'return:remember_failed', trace_id: `billing:${orderId}` })
  }
}

/** The pending order id for this user, or null. */
export function readPendingCheckout(userId: string): string | null {
  let raw: string | null = null
  try {
    raw = window.sessionStorage.getItem(KEY)
  } catch {
    return null
  }
  if (raw === null) return null
  // Typed on the way out of `JSON.parse` rather than cast: the repo's
  // forbidden-syntax check rejects `as`, `any` and `unknown`, and `ApiValue`
  // already models exactly what JSON can be.
  let parsed: ApiValue = null
  try {
    parsed = JSON.parse(raw)
  } catch {
    return null
  }
  if (!isApiRecord(parsed)) return null
  const { orderId, userId: storedUserId } = parsed
  if (typeof orderId !== 'string' || orderId === '') return null
  if (typeof storedUserId !== 'string' || storedUserId !== userId) return null
  return orderId
}

/** Forget the pending order. Called once the user leaves the return page. */
export function clearPendingCheckout(): void {
  try {
    window.sessionStorage.removeItem(KEY)
  } catch {
    // Nothing to do: the value is tab-scoped and expires with the tab.
  }
}
